<template>
  <HeadlessTransitionRoot as="template" :show="isRevealed">
    <HeadlessDialog as="div" class="relative z-10">
      <HeadlessTransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-background-fg/30 transition-opacity"
        />
      </HeadlessTransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div
          class="flex min-h-full items-center justify-center p-4 text-center"
        >
          <HeadlessTransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <HeadlessDialogPanel
              class="relative w-full overflow-hidden rounded-lg bg-background px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
            >
              <div
                class="flex w-full items-center rounded border border-warning p-2 text-sm leading-tight text-background-fg/87"
              >
                <IconExclamationCircle
                  class="mr-2 size-6 text-warning"
                />
                <p class="">
                  {{ errorMessage }}
                </p>
              </div>

              <template v-if="isRetrying">
                <div
                  class="flex w-full flex-col items-center"
                >
                  <p class="mt-4 text-background-fg/87">
                    Retrying payment sync
                  </p>
                  <IconLoading
                    class="size-12 animate-spin text-primary"
                  />
                </div>
              </template>
              <template v-else>
                <div>
                  <div
                    class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left"
                  >
                    <div class="mt-2">
                      <p class="mt-4 text-background-fg/87">
                        {{ paymentStatus }}, but the payment
                        status has not been updated.
                      </p>
                    </div>
                  </div>
                </div>

                <div class="mt-5 space-y-3">
                  <button
                    type="button"
                    class="relative inline-flex w-full justify-center rounded-md bg-primary px-4 py-2 text-base font-medium text-background shadow-sm"
                    :disabled="isRetrying"
                    @click="onRetry"
                  >
                    Retry
                  </button>
                  <PaymentRetryDrawerExtraActions
                    :is-disabled="isRetrying"
                    :show-network-settings="
                      errorCode === 'FailedToFetch' ||
                      errorCode === 'FetchTimedOut'
                    "
                    @ignore="onIgnore"
                  />
                </div>
              </template>
            </HeadlessDialogPanel>
          </HeadlessTransitionChild>
        </div>
      </div>
    </HeadlessDialog>
  </HeadlessTransitionRoot>
</template>

<script setup lang="ts">
import {
  type CreateTransactionBody,
  type CreateTransactionResponse,
  createTransaction,
} from "@/services/createTransaction"
import { useConfirmDialog } from "@vueuse/core"
import {
  ApiError,
  FailedToFetchError,
  FetchTimedOutError,
} from "@/services/api"

const { reveal, isRevealed, onReveal, confirm, cancel } =
  useConfirmDialog<
    Parameters<typeof createTransaction>,
    CreateTransactionResponse,
    undefined
  >()

defineExpose({
  reveal,
})

const paymentData = ref<CreateTransactionBody>()
const invoiceId = ref<string>()
onReveal(([invoiceId_, transIn]) => {
  invoiceId.value = invoiceId_
  paymentData.value = transIn
  automateRetry()
})

const paymentStatus = computed(() =>
  paymentData.value?.source === "pos_cash" ||
  paymentData.value?.source === "pos_cash_split" ||
  paymentData.value?.data.transApproved
    ? "Payment Successful"
    : "Payment Failed"
)
const isRetrying = ref(false)
const onRetry = async () => {
  if (isRetrying.value === true) return
  try {
    isRetrying.value = true
    confirm(await syncTransaction())
  } catch {
    return
  } finally {
    isRetrying.value = false
  }
}

const automateRetry = async (
  remainingRetries = 5,
  sleepTime = 0.4
) => {
  if (remainingRetries <= 0) {
    isRetrying.value = false
    return
  }
  try {
    isRetrying.value = true
    confirm(await syncTransaction())
  } catch {
    await sleep(sleepTime)
    await automateRetry(remainingRetries - 1, sleepTime)
  } finally {
    isRetrying.value = false
  }
}

const syncTransaction = async () => {
  if (!invoiceId.value || !paymentData.value) {
    cancel()
  } else {
    try {
      const response = await createTransaction(
        invoiceId.value,
        paymentData.value
      )
      errorCode.value = null

      return response
    } catch (error) {
      if (error instanceof FetchTimedOutError) {
        errorCode.value = "FetchTimedOut"
      } else if (error instanceof FailedToFetchError) {
        errorCode.value = "FailedToFetch"
      } else if (error instanceof ApiError) {
        errorCode.value =
          Number(error.code) === 502
            ? "ServerError"
            : "APIError"
      } else {
        errorCode.value = "unknown"
      }
      // Rethrow for automated retry
      throw error
    }
  }
}

const errorCode = ref<
  | "FailedToFetch"
  | "ServerError"
  | "FetchTimedOut"
  | "APIError"
  | "unknown"
  | null
>(null)
const errorMessage = computed(() =>
  errorCode.value
    ? {
        FailedToFetch:
          "Failed to sync payment status - this might be an issue with your internet connection",
        FetchTimedOut:
          "Failed to sync payment status - this might be an issue with your internet connection",
        ServerError:
          "Failed to sync payment status - please retry",
        APIError: "Failed to sync payment status",
        unknown: "Failed to sync payment status",
      }[errorCode.value]
    : undefined
)
const onIgnore = async () => {
  cancel()
}
</script>
